import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import Form from "react-bootstrap/Form";
import {
  FaUserEdit,
  FaIdCard,
  FaCheckCircle,
  FaArrowLeft,
  FaUser,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const STAFF_ROLES = [
  "Teacher",
  "Clerk",
  "Class Teacher",
  "Clerk And Techer",
  "Director And Principal",
  "Director And Voice Principal",
  "Faculty",
  "LDA",
  "UDA",
  "Tutor And LDA",
  "Academic Incharge",
  "Child Caretaker",
  "Mother Teacher",
  "Computer Operator",
  "Head Master",
  "Physical Teacher",
  "Office Bearers",
  "Driver",
  "Helper",
];

const LOGIN_TYPES = ["Admin", "Teacher", "Clerk", "None"];
const RELIGIONS = [
  "Hindu",
  "Muslim",
  "Christian",
  "Sikh",
  "Buddhist",
  "Jain",
  "Other",
];
const GENDERS = ["Male", "Female"];

function toDateInput(value) {
  if (value == null || String(value).trim() === "") return "";
  const s = String(value).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={`sr-field${className ? ` ${className}` : ""}`}>
      <label className="sr-field__label">
        {label}
        {required ? <span className="sr-field__req">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="sr-section">
      <div className="sr-section__head">
        <span className="sr-section__icon" aria-hidden>
          <Icon />
        </span>
        <div className="min-w-0">
          <h2 className="sr-section__title">{title}</h2>
          {subtitle ? <p className="sr-section__sub">{subtitle}</p> : null}
        </div>
      </div>
      <div className="sr-section__body">{children}</div>
    </section>
  );
}

function mapStaffToForm(staffData) {
  return {
    staffName: staffData.staff_name || "",
    department_id: staffData.department_id || "",
    dob: toDateInput(staffData.staff_dob),
    doj: toDateInput(staffData.joining_date),
    religion: staffData.staff_religion || staffData.religion || "",
    gender: staffData.staff_gender || "",
    AdharNo: staffData.aadhar_no || "",
    nationality: staffData.nationality || "",
    staffRole: staffData.staff_role || staffData.staffRole || "",
    loginType: staffData.login_type || staffData.loginType || "",
    phone: staffData.staff_phone || "",
    email: staffData.staff_email || "",
    staffAddress: staffData.staff_address || "",
  };
}

const EditStaff = () => {
  const location = useLocation();
  const history = useHistory();
  const staffData = location.state?.staffData || null;
  const staffId = staffData?.staff_id || staffData?.id || "";

  const [formData, setFormData] = useState(() =>
    staffData ? mapStaffToForm(staffData) : mapStaffToForm({})
  );
  const [mDepartment, setMDepartment] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const profilePath = staffId
    ? `/AdminMain/StaffProfile/${encodeURIComponent(String(staffId))}`
    : "/AdminMain/StaffList/MDL012";

  const selectedDepartmentName = useMemo(() => {
    return (
      mDepartment.find(
        (d) => String(d.department_id) === String(formData.department_id)
      )?.department_name ||
      staffData?.department_name ||
      ""
    );
  }, [mDepartment, formData.department_id, staffData]);

  const getDepartment = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setMDepartment([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE()}getDepartment.php`, {
        params: { school_code: schoolCode },
      });
      if (response.data.api_status === "OK") {
        setMDepartment(response.data.department || []);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootLoading(true);
      if (staffData) {
        setFormData(mapStaffToForm(staffData));
      }
      await getDepartment();
      if (!cancelled) setBootLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [staffData, getDepartment]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const regChange = (e) => {
    updateField(e.target.id, e.target.value);
  };

  const isValidInput = () => {
    if (!formData.staffName?.trim()) {
      myToast.notify_warning("Please enter staff name.");
      return false;
    }
    if (!formData.phone?.trim()) {
      myToast.notify_warning("Please enter phone number.");
      return false;
    }
    if (!formData.email?.trim()) {
      myToast.notify_warning("Please enter email address.");
      return false;
    }
    if (!staffId) {
      myToast.notify_warning("Missing staff ID.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isValidInput()) return;

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("staff_id", String(staffId));
      body.append("StaffName", formData.staffName.trim());
      body.append("phone", formData.phone.trim());
      body.append("email", formData.email.trim());
      body.append("Department_id", formData.department_id || "");
      body.append("dob", formData.dob || "");
      body.append("doj", formData.doj || "");
      body.append("gender", formData.gender || "");
      body.append("religion", formData.religion || "");
      body.append("aadhar_no", formData.AdharNo || "");
      body.append("nationality", formData.nationality || "");
      body.append("role", formData.staffRole || "");
      body.append("login_type", formData.loginType || "");
      body.append("address", formData.staffAddress || "");

      const response = await axios.post(
        `${API_BASE()}updateStaffPersonalDetails.php`,
        body
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Staff details updated successfully.");
        history.push(profilePath);
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Update failed."
        );
      }
    } catch (error) {
      console.error("Error updating staff:", error);
      myToast.notify_error("Error updating staff details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (bootLoading) return <MyLoader />;

  if (!staffData || !staffId) {
    return (
      <div
        className="content payroll-page sm-page sr-page es-page"
        style={{ background: COLORS.white }}
      >
        <div
          className="content-header"
          style={{ background: COLORS.primary_color_shade5 }}
        >
          <div className="leftside-content-header">
            <ul className="breadcrumbs">
              <li>
                <i
                  className="fa fa-home"
                  aria-hidden="true"
                  style={{ color: COLORS.white }}
                />
                <span style={{ color: COLORS.white }}> Edit Staff</span>
              </li>
            </ul>
          </div>
          <div className="rightside-content-header">
            <BackButton />
          </div>
        </div>
        <div className="bg_page_white_rounded box_shadow sm-shell">
          <div className="es-empty">
            <p className="es-empty__title">No staff selected</p>
            <p className="es-empty__hint">
              Open a staff profile and tap Edit to update details.
            </p>
            <Link
              to="/AdminMain/StaffList/MDL012"
              className="sr-btn sr-btn--primary"
            >
              <FaArrowLeft aria-hidden /> Back to Staff List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="content payroll-page sm-page sr-page es-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Edit Staff</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUserEdit />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Edit Staff</h1>
              <p className="sm-hero__sub">
                Update personal details for this staff member
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to={profilePath} className="sr-hero-link">
              <FaArrowLeft aria-hidden /> Profile
            </Link>
            <Link
              to="/AdminMain/StaffList/MDL012"
              className="sr-hero-link sr-hero-link--solid"
            >
              Staff List
            </Link>
          </div>
        </header>

        <div className="sr-body es-body">
          <div className="sr-profile-strip">
            <div className="sr-avatar es-avatar" aria-hidden>
              {staffData.staff_image_path ? (
                <img
                  src={staffData.staff_image_path}
                  alt=""
                  className="es-avatar-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.hidden = false;
                  }}
                />
              ) : null}
              <span
                className="es-avatar-fallback"
                hidden={Boolean(staffData.staff_image_path)}
              >
                {staffInitials(formData.staffName)}
              </span>
            </div>
            <div className="sr-profile-strip__meta min-w-0">
              <div className="sr-profile-strip__name">
                {formData.staffName?.trim() || "Staff member"}
              </div>
              <div className="sr-profile-strip__role">
                {[formData.staffRole, selectedDepartmentName, staffId]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <span className="es-id-chip">
              <FaUser aria-hidden />
              {staffId}
            </span>
          </div>

          <SectionCard
            icon={FaIdCard}
            title="Personal Information"
            subtitle="Changes are saved to the staff profile"
          >
            <div className="sr-stack">
              <div className="sr-grid sr-grid--1">
                <Field label="Staff Name" required>
                  <input
                    type="text"
                    id="staffName"
                    placeholder="Enter full name"
                    value={formData.staffName}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--4">
                <Field label="Department">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) =>
                      updateField("department_id", e.target.value)
                    }
                    value={formData.department_id}
                  >
                    <option value="">Select department</option>
                    {mDepartment.map((item, index) => (
                      <option
                        key={item.department_id || index}
                        value={item.department_id}
                      >
                        {item.department_name}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Staff Role">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("staffRole", e.target.value)}
                    value={formData.staffRole}
                  >
                    <option value="">Select role</option>
                    {STAFF_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Login Type">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("loginType", e.target.value)}
                    value={formData.loginType}
                  >
                    <option value="">Select login type</option>
                    {LOGIN_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Religion">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("religion", e.target.value)}
                    value={formData.religion}
                  >
                    <option value="">Select religion</option>
                    {RELIGIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Form.Select>
                </Field>
              </div>

              <div className="sr-grid sr-grid--4">
                <Field label="Phone Number" required>
                  <input
                    type="tel"
                    id="phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Email Address" required>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Date of Birth">
                  <input
                    type="date"
                    id="dob"
                    value={formData.dob}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Date of Joining">
                  <input
                    type="date"
                    id="doj"
                    value={formData.doj}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--3">
                <Field label="Gender">
                  <div className="sr-chip-row" role="group" aria-label="Gender">
                    {GENDERS.map((g) => (
                      <button
                        key={g}
                        type="button"
                        className={`sr-choice-chip${
                          formData.gender === g ? " is-active" : ""
                        }`}
                        onClick={() => updateField("gender", g)}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Aadhaar Number">
                  <input
                    type="text"
                    id="AdharNo"
                    placeholder="Enter Aadhaar number"
                    value={formData.AdharNo}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Nationality">
                  <input
                    type="text"
                    id="nationality"
                    placeholder="Enter nationality"
                    value={formData.nationality}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--1">
                <Field label="Staff Address">
                  <textarea
                    id="staffAddress"
                    rows={3}
                    className="sr-input sr-textarea"
                    placeholder="Enter full address"
                    value={formData.staffAddress}
                    onChange={regChange}
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <div className="sr-actions">
            <Link to={profilePath} className="sr-btn sr-btn--ghost">
              Cancel
            </Link>
            <button
              type="button"
              className="sr-btn sr-btn--primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              <FaCheckCircle aria-hidden />
              {submitting ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStaff;
